import React, { useMemo, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

const ChartWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  background: 'linear-gradient(135deg, rgba(11, 145, 214, 0.08) 0%, rgba(11, 145, 214, 0.02) 100%)',
  border: '1px solid rgba(11, 145, 214, 0.12)',
  borderRadius: 24,
  padding: theme.spacing(3.5),
  boxShadow: '0 24px 48px -20px rgba(16, 66, 152, 0.18)',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
    borderRadius: 20
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    borderRadius: 16
  }
}));

const ChartHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
  flexWrap: 'wrap',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2)
  }
}));

const SummaryGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  flexWrap: 'wrap',
  alignItems: 'center'
}));

const SummaryPill = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.75),
  borderRadius: 9999,
  background: 'rgba(255, 255, 255, 0.85)',
  border: '1px solid rgba(11, 145, 214, 0.15)',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontWeight: 600,
  fontSize: 13,
  color: '#0B91D6',
  [theme.breakpoints.down('sm')]: {
    fontSize: 12
  }
}));

const ChartSurface = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minHeight: 320,
  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.6) 100%)',
  borderRadius: 18,
  border: '1px solid rgba(11, 145, 214, 0.12)',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    minHeight: 280
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 260
  }
}));

const LegendRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2.5)
  }
}));

const LegendItems = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2.5),
  alignItems: 'center'
}));

const LegendItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  fontSize: 13,
  fontWeight: 500,
  color: '#2F3A4C',
  [theme.breakpoints.down('sm')]: {
    fontSize: 12
  }
}));

const LegendDot = styled('span')({
  display: 'inline-block',
  width: 12,
  height: 12,
  borderRadius: '50%'
});

const EmptyState = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none'
}));

const DynamicMonthlyChart = ({ reputationData, selectedPeriod }) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

  const data = reputationData || {
    yourBusiness: { rating: 4.2, reviewCount: 0 }
  };

  const baseRating = Number(data?.yourBusiness?.rating) || 4.2;
  const totalReviewCount = Number(data?.yourBusiness?.reviewCount) || 0;

  const periodLabels = useMemo(
    () => ({
      '3M': 'Last 3 Months',
      '6M': 'Last 6 Months',
      '1Y': 'Last 12 Months',
      'ALL': 'All Time'
    }),
    []
  );

  const starColors = useMemo(
    () => ({
      5: '#0B91D6',
      4: '#2AB1E4',
      3: '#5FD0F5',
      2: '#FFC85C',
      1: '#FF7A70'
    }),
    []
  );

  const selectedPeriodLabel = periodLabels[selectedPeriod] || selectedPeriod || 'Selected Period';

  const getMonthsFromPeriod = (period) => {
    switch (period) {
      case '3M':
        return 3;
      case '6M':
        return 6;
      case '1Y':
        return 12;
      case 'ALL':
        return 24;
      default:
        return 6;
    }
  };

  const months = getMonthsFromPeriod(selectedPeriod);
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const reviews = Array.isArray(data?.reviews) ? data.reviews : data?.reviews?.reviews || [];

  const convertStarRating = (starRating) => {
    if (typeof starRating === 'number') return starRating;
    const starMap = {
      ONE: 1,
      TWO: 2,
      THREE: 3,
      FOUR: 4,
      FIVE: 5
    };
    return starMap[starRating] || 0;
  };

  const generateMonthlyData = () => {
    const chartData = [];
    const currentMonth = new Date().getMonth();

    if (reviews.length === 0) {
      for (let i = 0; i < months; i += 1) {
        const monthIndex = (currentMonth - months + 1 + i + 12) % 12;
        chartData.push({
          month: monthNames[monthIndex],
          totalReviews: 0,
          starRatings: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
        });
      }
      return chartData;
    }

    const reviewsByMonth = {};

    reviews.forEach((review) => {
      let reviewDate;
      if (review.createTime) {
        reviewDate = new Date(review.createTime);
      } else if (review.updateTime) {
        reviewDate = new Date(review.updateTime);
      } else {
        const randomMonthsAgo = Math.floor(Math.random() * months);
        reviewDate = new Date();
        reviewDate.setMonth(reviewDate.getMonth() - randomMonthsAgo);
      }

      const monthKey = `${reviewDate.getFullYear()}-${reviewDate.getMonth()}`;
      if (!reviewsByMonth[monthKey]) {
        reviewsByMonth[monthKey] = [];
      }
      reviewsByMonth[monthKey].push(review);
    });

    if (selectedPeriod === 'ALL') {
      const allMonthKeys = Object.keys(reviewsByMonth).sort();
      const maxMonths = Math.min(allMonthKeys.length, 24);

      allMonthKeys.slice(-maxMonths).forEach((monthKey) => {
        const [, monthIndex] = monthKey.split('-').map(Number);
        const monthReviews = reviewsByMonth[monthKey] || [];
        const starDistribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

        monthReviews.forEach((review) => {
          const starRating = convertStarRating(review.starRating);
          if (starRating >= 1 && starRating <= 5) {
            starDistribution[starRating] += 1;
          }
        });

        chartData.push({
          month: monthNames[monthIndex],
          totalReviews: monthReviews.length,
          starRatings: starDistribution
        });
      });
    } else {
      for (let i = 0; i < months; i += 1) {
        const currentYear = new Date().getFullYear();
        const monthIndex = (currentMonth - months + 1 + i + 12) % 12;
        const monthKey = `${currentYear}-${monthIndex}`;

        const monthReviews = reviewsByMonth[monthKey] || [];
        const starDistribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

        monthReviews.forEach((review) => {
          const starRating = convertStarRating(review.starRating);
          if (starRating >= 1 && starRating <= 5) {
            starDistribution[starRating] += 1;
          }
        });

        chartData.push({
          month: monthNames[monthIndex],
          totalReviews: monthReviews.length,
          starRatings: starDistribution
        });
      }
    }

    return chartData;
  };

  const generateDailyData = (reviewList) => {
    const dailyData = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const reviewsByDay = {};

    reviewList.forEach((review) => {
      let reviewDate;
      if (review.createTime) {
        reviewDate = new Date(review.createTime);
      } else if (review.updateTime) {
        reviewDate = new Date(review.updateTime);
      } else {
        return;
      }

      if (reviewDate.getFullYear() === currentYear && reviewDate.getMonth() === currentMonth) {
        const dayKey = reviewDate.getDate();
        if (!reviewsByDay[dayKey]) {
          reviewsByDay[dayKey] = [];
        }
        reviewsByDay[dayKey].push(review);
      }
    });

    for (let day = 1; day <= daysInMonth; day += 1) {
      const dayReviews = reviewsByDay[day] || [];
      const starDistribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

      dayReviews.forEach((review) => {
        const starRating = convertStarRating(review.starRating);
        if (starRating >= 1 && starRating <= 5) {
          starDistribution[starRating] += 1;
        }
      });

      dailyData.push({
        month: day.toString(),
        totalReviews: dayReviews.length,
        starRatings: starDistribution
      });
    }

    return dailyData;
  };

  const chartData = useMemo(() => {
    if (selectedPeriod === '1 Month') {
      return generateDailyData(reviews);
    }
    return generateMonthlyData();
  }, [selectedPeriod, reviews, months]);

  const maxReviewCount = chartData.length ? Math.max(...chartData.map((p) => p.totalReviews)) : 0;
  const safeMax = maxReviewCount || 1;
  const noData = chartData.every((point) => point.totalReviews === 0);

  const chartWidth = isSmDown ? 360 : isMdDown ? 720 : 1120;
  const chartHeight = isSmDown ? 520 : isMdDown ? 440 : 360;
  const margin = useMemo(
    () => ({
      top: isSmDown ? 30 : isMdDown ? 40 : 48,
      right: isSmDown ? 32 : isMdDown ? 56 : 72,
      bottom: isSmDown ? 44 : isMdDown ? 68 : 88,
      left: isSmDown ? 52 : isMdDown ? 78 : 92
    }),
    [isSmDown, isMdDown]
  );
  const plotWidth = chartWidth - margin.left - margin.right;
  const plotHeight = chartHeight - margin.top - margin.bottom;

  const barSlotWidth = chartData.length > 0 ? plotWidth / chartData.length : plotWidth;
  const barWidth = chartData.length > 0 ? Math.min(54, Math.max(26, barSlotWidth * 0.55)) : 40;

  const yTicks = useMemo(() => {
    if (!chartData.length) {
      return [0, 5, 10, 15, 20];
    }
    const tickCount = 4;
    const rawStep = safeMax / tickCount;
    const magnitude = 10 ** Math.floor(Math.log10(rawStep || 1));
    const step = Math.ceil(rawStep / magnitude) * magnitude;
    return Array.from({ length: tickCount + 1 }, (_, index) => index * step);
  }, [chartData.length, safeMax]);

  const [hoveredIndex, setHoveredIndex] = useState(null);
  const axisY = margin.top + plotHeight;
  const axisTextYOffset = selectedPeriod === '1 Month' ? (isSmDown ? 34 : 34) : 26;

  return (
    <ChartWrapper>
      <ChartHeader>
        <Box>
          <Typography
            component="h3"
            sx={{
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              fontSize: { xs: 18, md: 20 },
              fontWeight: 600,
              color: '#1B2735',
              marginBottom: 0.5
            }}
          >
            Review Trends Over Time
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              fontSize: { xs: 12.5, md: 13.5 },
              color: '#546173',
              maxWidth: 540
            }}
          >
            Visualise how review volume and sentiment shift across your chosen period to pinpoint momentum and opportunities to respond faster.
          </Typography>
        </Box>
        <SummaryGroup>
          <SummaryPill>{selectedPeriodLabel}</SummaryPill>
          <SummaryPill>Total Reviews · {totalReviewCount}</SummaryPill>
          <SummaryPill>Avg Rating · {baseRating.toFixed(1)}</SummaryPill>
        </SummaryGroup>
      </ChartHeader>

      <ChartSurface sx={{ height: chartHeight }}>
        <svg
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          preserveAspectRatio="none"
          style={{ display: 'block' }}
        >
          <defs>
            <linearGradient id="chartBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(11, 145, 214, 0.08)" />
              <stop offset="100%" stopColor="rgba(11, 145, 214, 0.02)" />
            </linearGradient>
            <linearGradient id="barGloss" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="rgba(255,255,255,0.35)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </linearGradient>
            <filter id="barShadow" x="-20%" y="-20%" width="140%" height="160%">
              <feDropShadow dx="0" dy="10" stdDeviation="12" floodColor="rgba(14, 81, 134, 0.15)" />
            </filter>
          </defs>

          <rect
            x="0"
            y="0"
            width={chartWidth}
            height={chartHeight}
            fill="url(#chartBgGradient)"
            rx="22"
          />

          <rect
            x={margin.left}
            y={margin.top}
            width={plotWidth}
            height={plotHeight}
            fill="rgba(255, 255, 255, 0.85)"
            stroke="rgba(11, 145, 214, 0.12)"
            strokeWidth="1"
            rx="18"
          />

          {yTicks.map((tick) => {
            const tickValue = Math.min(tick, safeMax);
            const yPos = margin.top + plotHeight - (tickValue / safeMax) * plotHeight;
            return (
              <g key={`grid-${tick}`}>
                <line
                  x1={margin.left}
                  y1={yPos}
                  x2={margin.left + plotWidth}
                  y2={yPos}
                  stroke="rgba(11, 145, 214, 0.12)"
                  strokeWidth={tick === 0 ? 1.2 : 0.8}
                  strokeDasharray={tick === 0 ? '0' : '6 10'}
                />
                <text
                  x={margin.left - 20}
                  y={yPos + 5}
                  textAnchor="end"
                  fontSize="12"
                  fontFamily='Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                  fill="#52616B"
                >
                  {tick}
                </text>
              </g>
            );
          })}

          <line
            x1={margin.left}
            y1={axisY}
            x2={margin.left + plotWidth}
            y2={axisY}
            stroke="rgba(15, 29, 47, 0.22)"
            strokeWidth="1.2"
          />

          {chartData.map((point, index) => {
            const barGroupX = margin.left + index * barSlotWidth + (barSlotWidth - barWidth) / 2;
            const totalBarHeight = maxReviewCount > 0 ? (point.totalReviews / safeMax) * plotHeight : 0;
            let currentY = plotHeight;
            const segments = [];

            [1, 2, 3, 4, 5].forEach((star) => {
              const count = point.starRatings?.[star] || 0;
              if (point.totalReviews > 0 && count > 0) {
                const segmentHeight = (count / point.totalReviews) * totalBarHeight;
                segments.push({
                  star,
                  count,
                  height: segmentHeight,
                  y: currentY - segmentHeight
                });
                currentY -= segmentHeight;
              }
            });

            const barCenter = barGroupX + barWidth / 2;
            const label = selectedPeriod === '1 Month' ? `${index + 1} ${monthNames[new Date().getMonth()]}` : point.month;
            const isHovered = hoveredIndex === index;

            return (
              <g
                key={`bar-${point.month}-${index}`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {segments.length > 0 && (
                  <g transform={`translate(${barGroupX}, ${margin.top})`} filter="url(#barShadow)">
                    {segments.map((segment, segIndex) => (
                      <g key={`${index}-${segment.star}`}>
                        <rect
                          x={0}
                          y={segment.y}
                          width={barWidth}
                          height={segment.height}
                          fill={starColors[segment.star]}
                          fillOpacity={isHovered || hoveredIndex === null ? 1 : 0.45}
                          rx={segIndex === segments.length - 1 ? 14 : 0}
                          ry={segIndex === segments.length - 1 ? 14 : 0}
                        >
                          <title>
                            {`${segment.count} review${segment.count === 1 ? '' : 's'} · ${segment.star}-Star`}
                          </title>
                        </rect>
                        <rect
                          x={0}
                          y={segment.y}
                          width={barWidth}
                          height={Math.min(segment.height, 24)}
                          fill="url(#barGloss)"
                          opacity={0.25}
                          rx={segIndex === segments.length - 1 ? 14 : 0}
                          ry={segIndex === segments.length - 1 ? 14 : 0}
                        />
                      </g>
                    ))}

                    {point.totalReviews > 0 && (
                      <g>
                        <rect
                          x={(barWidth - 58) / 2}
                          y={Math.max(currentY - 18, -38)}
                          width={58}
                          height={28}
                          rx={14}
                          ry={14}
                          fill="rgba(11, 145, 214, 0.16)"
                          stroke="rgba(11, 145, 214, 0.35)"
                        />
                        <text
                          x={barWidth / 2}
                          y={Math.max(currentY, -22)}
                          fill="#0B91D6"
                          fontSize="12"
                          fontWeight="600"
                          textAnchor="middle"
                          fontFamily='Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                        >
                          {point.totalReviews}
                        </text>
                      </g>
                    )}
                  </g>
                )}

                {point.totalReviews === 0 && (
                  <g transform={`translate(${barGroupX}, ${margin.top})`}>
                    <rect
                      x={0}
                      y={plotHeight - 8}
                      width={barWidth}
                      height={8}
                      fill="rgba(82, 97, 107, 0.1)"
                      rx={4}
                    />
                  </g>
                )}

                <text
                  x={barCenter}
                  y={axisY + axisTextYOffset}
                  fill="#33404F"
                  fontSize={selectedPeriod === '1 Month' ? 11 : 13}
                  textAnchor={selectedPeriod === '1 Month' ? 'end' : 'middle'}
                  fontFamily='Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                  transform={
                    selectedPeriod === '1 Month'
                      ? `rotate(-45 ${barCenter} ${axisY + axisTextYOffset})`
                      : undefined
                  }
                >
                  {label}
                </text>
              </g>
            );
          })}

          {noData && (
            <text
              x={chartWidth / 2}
              y={margin.top + plotHeight / 2}
              textAnchor="middle"
              fontSize="15"
              fill="rgba(82, 97, 107, 0.65)"
              fontFamily='Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            >
              No reviews recorded for this period yet.
            </text>
          )}
        </svg>

        {noData && (
          <EmptyState>
            <Typography
              sx={{
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                fontSize: 13,
                fontWeight: 500,
                color: '#546173'
              }}
            >
              Try expanding the date range to see historical review trends.
            </Typography>
          </EmptyState>
        )}
      </ChartSurface>

      <LegendRow>
        <LegendItems>
          {[5, 4, 3, 2, 1].map((stars) => (
            <LegendItem key={`legend-${stars}`}>
              <LegendDot style={{ backgroundColor: starColors[stars] }} />
              {stars} Star
            </LegendItem>
          ))}
        </LegendItems>
        <Typography
          sx={{
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            fontSize: { xs: 12, sm: 12.5 },
            color: '#627185'
          }}
        >
          Hover any bar to focus the distribution for that month.
        </Typography>
      </LegendRow>
    </ChartWrapper>
  );
};

export default DynamicMonthlyChart;
